import React, { useState, useEffect } from 'react';
import { Manufacturer } from '../types';
import { ManufacturerService } from '../services/api';
import AdminRoute from '../components/AdminRoute';
import {
  PartsContainer,
  PartsTitle,
  SectionTitle,
  PartsTable,
  TableHeader,
  TableCell,
  PartDescription,
  BooleanIndicator,
  ActionButton,
  HeaderContainer,
  LoadingContainer,
  ErrorContainer
} from './PartsManagement.styles';

const ManufacturerManagement: React.FC = () => {
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingManufacturer, setEditingManufacturer] = useState<Manufacturer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    isActive: true
  });

  // Load manufacturers from API
  useEffect(() => {
    loadManufacturers();
  }, []);

  const loadManufacturers = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedManufacturers = await ManufacturerService.getAllManufacturers();
      setManufacturers(fetchedManufacturers);
    } catch (error) {
      console.error('Error loading manufacturers:', error);
      setError('Failed to load manufacturers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredManufacturers = manufacturers.filter(manufacturer =>
    manufacturer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (manufacturer.description && manufacturer.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (manufacturer.website && manufacturer.website.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCreateManufacturer = () => {
    setEditingManufacturer(null);
    setFormData({
      name: '',
      description: '',
      website: '',
      isActive: true
    });
    setShowForm(true);
  };

  const handleEditManufacturer = (manufacturer: Manufacturer) => {
    setEditingManufacturer(manufacturer);
    setFormData({
      name: manufacturer.name,
      description: manufacturer.description || '',
      website: manufacturer.website || '',
      isActive: manufacturer.isActive
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      website: '',
      isActive: true
    });
    setEditingManufacturer(null);
    setShowForm(false);
  };

  const handleSaveManufacturer = async () => {
    try {
      setError(null);
      
      if (editingManufacturer) {
        // Update existing manufacturer
        const updatedManufacturer = await ManufacturerService.updateManufacturer(editingManufacturer.id, {
          name: formData.name,
          description: formData.description,
          website: formData.website,
          isActive: formData.isActive
        });

        setManufacturers(manufacturers.map(m => m.id === editingManufacturer.id ? updatedManufacturer : m));
      } else {
        // Create new manufacturer
        const newManufacturer = await ManufacturerService.createManufacturer({
          name: formData.name,
          description: formData.description,
          website: formData.website,
          isActive: formData.isActive
        });

        setManufacturers([...manufacturers, newManufacturer]);
      }

      setShowForm(false);
    } catch (error) {
      console.error('Error saving manufacturer:', error);
      setError('Failed to save manufacturer. Please try again.');
    }
  };

  const handleDeleteManufacturer = async (manufacturerId: string) => {
    if (window.confirm('Are you sure you want to delete this manufacturer? This action cannot be undone.')) {
      try {
        setError(null);
        await ManufacturerService.deleteManufacturer(manufacturerId);
        setManufacturers(manufacturers.filter(m => m.id !== manufacturerId));
      } catch (error: any) {
        console.error('Error deleting manufacturer:', error);
        if (error.response?.status === 400) {
          setError('Cannot delete this manufacturer because it is currently in use by one or more models.');
        } else {
          setError('Failed to delete manufacturer. Please try again.');
        }
      }
    }
  };

  if (loading) {
    return (
      <AdminRoute>
        <PartsContainer>
          <PartsTitle>Manufacturer Management</PartsTitle>
          <LoadingContainer>Loading manufacturers...</LoadingContainer>
        </PartsContainer>
      </AdminRoute>
    );
  }

  return (
    <AdminRoute>
      <PartsContainer>
        <HeaderContainer>
          <PartsTitle>Manufacturer Management</PartsTitle>
          <ActionButton onClick={handleCreateManufacturer}>
            Add New Manufacturer
          </ActionButton>
        </HeaderContainer>
        
        {error && (
          <ErrorContainer data-testid="error-message">
            {error}
            <ActionButton onClick={() => setError(null)}>
              Dismiss
            </ActionButton>
          </ErrorContainer>
        )}

        <div className="form-section">
          <label htmlFor="search">Search Manufacturers</label>
          <input
            type="text"
            id="search"
            className="form-control"
            placeholder="Search by name, description, or website..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {showForm && (
          <div className="form-section">
            <SectionTitle>
              {editingManufacturer ? 'Edit Manufacturer' : 'Create New Manufacturer'}
            </SectionTitle>
            
            <form onSubmit={(e) => { e.preventDefault(); handleSaveManufacturer(); }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label htmlFor="name">Manufacturer Name *</label>
                  <input
                    type="text"
                    id="name"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="website">Website</label>
                  <input
                    type="url"
                    id="website"
                    className="form-control"
                    placeholder="https://example.com"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  className="form-control"
                  placeholder="Optional description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                  {' '}Active
                </label>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn btn-primary">
                  {editingManufacturer ? 'Update Manufacturer' : 'Create Manufacturer'}
                </button>
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="form-section">
          <SectionTitle>Manufacturers List ({filteredManufacturers.length} manufacturers)</SectionTitle>
          <PartsTable>
            <thead>
              <tr>
                <TableHeader>Name</TableHeader>
                <TableHeader>Website</TableHeader>
                <TableHeader>Description</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Created</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {filteredManufacturers.map(manufacturer => (
                <tr key={manufacturer.id}>
                  <TableCell>
                    <strong>{manufacturer.name}</strong>
                  </TableCell>
                  <TableCell>
                    {manufacturer.website ? (
                      <a 
                        href={manufacturer.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#0066cc', textDecoration: 'none' }}
                      >
                        {manufacturer.website}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    {manufacturer.description && (
                      <PartDescription>
                        {manufacturer.description}
                      </PartDescription>
                    )}
                  </TableCell>
                  <TableCell>
                    <BooleanIndicator $value={manufacturer.isActive}>
                      {manufacturer.isActive ? 'Active' : 'Inactive'}
                    </BooleanIndicator>
                  </TableCell>
                  <TableCell>
                    {new Date(manufacturer.createdDate).toLocaleDateString()}
                    <PartDescription>
                      by {manufacturer.createdBy}
                    </PartDescription>
                  </TableCell>
                  <TableCell>
                    <ActionButton
                      onClick={() => handleEditManufacturer(manufacturer)}
                    >
                      Edit
                    </ActionButton>
                    <ActionButton
                      $variant="danger"
                      onClick={() => handleDeleteManufacturer(manufacturer.id)}
                    >
                      Delete
                    </ActionButton>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </PartsTable>

          {filteredManufacturers.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              {searchTerm ? 'No manufacturers found matching your search.' : 'No manufacturers available. Create your first manufacturer.'}
            </div>
          )}
        </div>
      </PartsContainer>
    </AdminRoute>
  );
};

export default ManufacturerManagement;